/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, batch, reatomMap, withAssign } from '@reatom/framework'

import {
  Array,
  AsyncStateN,
  Iterable,
  Map,
  pipe,
  Result,
  Set,
} from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { walletBalanceUpdateEvent } from '@root/entities/realtime'
import { SecureStorageError } from '@root/features/secure-storage/secure-storage-error'
import { UnknownError, type Error } from '@root/shared'
import { atomWithPrev, reatomResource } from '@root/shared/lib'
import { SecureStorageErrorCode } from '@root/shared/ui/secure-storage-error-code'

import { WalletId } from '../model'

import { reatomUserWalletsBalances } from './reatom-user-wallets-balances'
import {
  reatomWallet,
  walletDtoUpdateReceivedAction,
  type WalletModel,
} from './reatom-wallet'
import { fetchWalletsRequestAction } from './wallet-requests'

import type { WalletAddress } from '../model'
import type { MapAtom } from '@reatom/framework'
import type { WalletApi } from '@root/shared/api'

// TODO: Move to features

export const reatomUserWallets = ({
  network,
}: {
  network: Network.Network
}) => {
  //#region State
  const walletsByIdStateAtom = reatomResource<
    Error.UnknownError,
    MapAtom<WalletId, WalletModel>
  >(
    async (ctx) => {
      const result = await ctx.schedule(() =>
        fetchWalletsRequestAction(ctx, network),
      )

      if (
        Result.isFailure(result) &&
        result.failure instanceof SecureStorageError &&
        (result.failure.cause === SecureStorageErrorCode.StorageNotFound ||
          result.failure.cause ===
            SecureStorageErrorCode.StorageNotFoundOrInitialized)
      ) {
        return Result.succeed(reatomMap<WalletId, WalletModel>([]))
      }

      const mappedResult = result.pipe(
        Result.mapError(UnknownError.fallbackToUnknown),
      )

      return mappedResult.pipe(
        Result.map((it) => {
          const byId = pipe(
            it,
            Iterable.map((it) => reatomWallet.create(ctx, it)),
            Iterable.toMap((it) => it.id),
          )

          return reatomMap(byId)
        }),
      )
    },
    {
      loadOnConnect: false,
      retry: {
        enabled: true,
        strategy: 'linear',
      },
    },
  )
  //#endregion

  //#region Computed
  const walletsStateAtom = atom((ctx) => {
    const state = ctx.spy(walletsByIdStateAtom)

    return state.pipe(
      AsyncStateN.map((it) => {
        const map = ctx.spy(it)
        return [...map.values()]
      }),
    )
  })
  const sortedWalletsStateAtom = atom((ctx) => {
    const state = ctx.spy(walletsStateAtom)
    return state.pipe(
      AsyncStateN.map((it) => {
        return Array.sort(reatomWallet.cmpByOrderAsc(ctx))(it)
      }),
    )
  })
  const balances = reatomUserWalletsBalances({ walletsStateAtom })

  const walletsByIdAtom = atom((ctx) => {
    const state = ctx.spy(walletsByIdStateAtom)

    return state.value
      ? ctx.spy(state.value)
      : Map.create<WalletId, WalletModel>()
  })
  const walletsByAddressAtom = atom((ctx) => {
    const wallets = ctx.spy(walletsAtom)

    return pipe(
      wallets,
      Iterable.toMap((it) => it.address),
    )
  })

  const walletsAtom = atom((ctx) => {
    return ctx.spy(sortedWalletsStateAtom).value ?? []
  })

  const favoriteWalletsAtom = atom((ctx) => {
    const wallets = ctx.spy(walletsAtom)
    return wallets.filter((wallet) => {
      const favorite = ctx.spy(wallet.favorite)
      return favorite
    })
  })

  const walletIdsAtom = atomWithPrev<Set<WalletId>>((ctx, prevIds) => {
    const byId = ctx.spy(walletsByIdAtom)
    const walletIds = Set.create(Map.keys(byId))
    if (prevIds && Set.equals(prevIds, walletIds)) {
      return prevIds
    }
    return walletIds
  }, 'walletIdsAtom')

  const walletAddressesAtom = atomWithPrev<Set<WalletAddress>>(
    (ctx, prevAddresses) => {
      const wallets = ctx.spy(walletsAtom)
      const walletAddresses = Set.create(wallets.map((it) => it.address))
      if (prevAddresses && Set.equals(prevAddresses, walletAddresses)) {
        return prevAddresses
      }
      return walletAddresses
    },
    'walletAddressesAtom',
  )

  //#endregion

  const refreshWalletsStateAtom = atom(
    AsyncStateN.notInitiated<Error.UnknownError, undefined>(),
    'refreshWalletsStateAtom',
  )

  const loadWalletsAction = action(async (ctx) => {
    const state = ctx.get(walletsByIdStateAtom)
    if (AsyncStateN.isSuccess(state)) {
      return
    }
    walletsByIdStateAtom.run(ctx)
  })

  const refreshWalletsAction = action(async (ctx) => {
    refreshWalletsStateAtom(ctx, AsyncStateN.loading())

    const result = await ctx.schedule(() =>
      fetchWalletsRequestAction(ctx, network),
    )
    const mappedResult = pipe(
      result,
      Result.mapError(UnknownError.fallbackToUnknown),
    )

    batch(ctx, () => {
      if (Result.isSuccess(mappedResult)) {
        for (const it of mappedResult.success) {
          upsertWalletAction(ctx, it)
        }
        refreshWalletsStateAtom(ctx, AsyncStateN.success(undefined))
        return
      }
      refreshWalletsStateAtom(ctx, AsyncStateN.error(mappedResult.failure))
    })
  })

  const upsertWalletAction = action((ctx, walletDto: WalletApi.WalletDto) => {
    const byId = ctx.get(walletsByIdStateAtom)
    if (!AsyncStateN.isSuccess(byId)) {
      return
    }
    const existingWallet = byId.value.get(ctx, WalletId(walletDto.walletId))
    if (existingWallet) {
      existingWallet.updateFromDto(ctx, walletDto)
      return
    }

    const newWallet = reatomWallet.create(ctx, walletDto)
    byId.value.set(ctx, WalletId(walletDto.walletId), newWallet)
  })

  const deleteWalletAction = action((ctx, walletId: WalletId) => {
    const byId = ctx.get(walletsByIdStateAtom)
    if (!AsyncStateN.isSuccess(byId)) {
      return
    }
    byId.value.delete(ctx, walletId)
  })

  const updateAction = action((ctx, dto: Array<WalletApi.WalletDto>) => {
    for (const it of dto) {
      upsertWalletAction(ctx, it)
    }
  })

  return walletsByIdStateAtom.pipe(
    withAssign(() => ({
      ...balances,
      network,
      walletsState: sortedWalletsStateAtom,
      wallets: walletsAtom,
      walletIds: walletIdsAtom,
      walletAddresses: walletAddressesAtom,
      walletsById: walletsByIdAtom,
      walletsByAddress: walletsByAddressAtom,
      favoriteWallets: favoriteWalletsAtom,
      load: loadWalletsAction,
      refresh: refreshWalletsAction,
      upsert: upsertWalletAction,
      delete: deleteWalletAction,
      update: updateAction,
    })),
  )
}

export type UserWalletsModel = ReturnType<typeof reatomUserWallets>

export const walletsModelByNetwork = Network.byNetworkRecord((network) =>
  reatomUserWallets({ network }),
)

export const walletsModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  return walletsModelByNetwork[network]
})

walletBalanceUpdateEvent.onCall((ctx, dto) => {
  const model = walletsModelByNetwork[Network.fromDto(dto.chain)]
  const byId = ctx.get(model.walletsById)
  // We skip wallets appends from this event, because it might emit a wallet update which is not on user device
  if (!byId.has(dto.walletId)) {
    return
  }
  model.upsert(ctx, dto)
})

walletDtoUpdateReceivedAction.onCall((ctx, walletDto) => {
  const model = walletsModelByNetwork[Network.fromDto(walletDto.chain)]
  const byId = ctx.get(model.walletsById)
  const wallet = byId.get(WalletId(walletDto.walletId))
  if (!wallet) {
    return
  }
  wallet.updateFromDto(ctx, walletDto)
})

//#region Utility selectors

export const walletsStateAtom = atom(
  (ctx) => ctx.spy(ctx.spy(walletsModelAtom).walletsState),
  'walletsStateAtom',
)
export const walletsByIdAtom = atom((ctx) =>
  ctx.spy(ctx.spy(walletsModelAtom).walletsById),
)
export const walletsByAddressAtom = atom((ctx) =>
  ctx.spy(ctx.spy(walletsModelAtom).walletsByAddress),
)
export const walletsAtom = atom(
  (ctx) => ctx.spy(ctx.spy(walletsModelAtom).wallets),
  'walletsAtom',
)
export const walletIdsAtom = atom(
  (ctx) => ctx.spy(ctx.spy(walletsModelAtom).walletIds),
  'walletsAtom',
)
export const walletAddressesAtom = atom(
  (ctx) => ctx.spy(ctx.spy(walletsModelAtom).walletAddresses),
  'walletsAtom',
)
export const walletsCountAtom = atom(
  (ctx) => ctx.spy(walletsAtom).length,
  'walletsCountAtom',
)

//#endregion

export const refreshWalletsAction = action((ctx) => {
  const { refresh } = ctx.get(walletsModelAtom)
  refresh(ctx)
}, 'refreshWalletsAction')

/**
 * Ask for this account's wallets, once.
 *
 * The list does not load when something subscribes to it — it waits to be
 * asked, and the thing that asked was the old desktop layout. The V2 screens
 * draw their own chrome and sit outside that layout, so nobody asked and they
 * showed an account with no wallets to somebody who had them.
 *
 * Does nothing if the list has already arrived, so mounting a second screen
 * costs no request.
 */
export const loadWalletsAction = action((ctx) => {
  const { load } = ctx.get(walletsModelAtom)
  void load(ctx)
}, 'loadWalletsAction')
